class SessionTimeout {
  constructor() {
    this.timeoutDuration = 60 * 60 * 1000; // 60 minutes (1 hour) in milliseconds
    this.warningDuration = 2 * 60 * 1000;  // Show warning 2 minutes before timeout
    this.timeoutId = null;
    this.warningTimeoutId = null;
    this.warningCallback = null;
    this.logoutCallback = null;
    this.isActive = false;
    this.lastActivity = Date.now();
    
    // Bind methods to maintain context
    this.handleActivity = this.handleActivity.bind(this);
    this.checkActivity = this.checkActivity.bind(this);
  }

  /**
   * Start the session timeout
   * @param {Function} warningCallback - Function to call when showing warning
   * @param {Function} logoutCallback - Function to call when session expires
   */
  start(warningCallback, logoutCallback) {
    this.warningCallback = warningCallback;
    this.logoutCallback = logoutCallback;
    this.isActive = true;
    this.lastActivity = Date.now();
    
    this.setupActivityListeners();
    this.resetTimer();
  }

  /**
   * Stop the session timeout
   */
  stop() {
    this.isActive = false;
    this.clearTimers();
    this.removeActivityListeners();
  }

  /**
   * Reset the timeout timer
   */
  resetTimer() {
    if (!this.isActive) return;

    this.clearTimers();
    this.lastActivity = Date.now();

    // Set warning timer (show warning 2 minutes before logout)
    this.warningTimeoutId = setTimeout(() => {
      if (this.isActive && this.warningCallback) {
        this.warningCallback();
      }
    }, this.timeoutDuration - this.warningDuration);

    // Set logout timer
    this.timeoutId = setTimeout(() => {
      if (this.isActive && this.logoutCallback) {
        this.logoutCallback();
      }
    }, this.timeoutDuration);
  }

  /**
   * Clear all timers
   */
  clearTimers() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    if (this.warningTimeoutId) {
      clearTimeout(this.warningTimeoutId);
      this.warningTimeoutId = null;
    }
  }

  /**
   * Handle user activity
   */
  handleActivity() {
    if (!this.isActive) return;
    
    const now = Date.now();
    const timeSinceLastActivity = now - this.lastActivity;
    
    // Only reset if enough time has passed to avoid too frequent resets
    if (timeSinceLastActivity > 30000) { // 30 seconds threshold
      console.log('User activity detected - resetting timer');
      this.resetTimer();
    }
  }

  /**
   * Setup activity listeners
   */
  setupActivityListeners() {
    const events = [
      'mousedown',
      'mousemove', 
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    events.forEach(event => {
      document.addEventListener(event, this.handleActivity, true);
    });

    // Also listen for route changes
    window.addEventListener('beforeunload', this.handleActivity);
  }

  /**
   * Remove activity listeners
   */
  removeActivityListeners() {
    const events = [
      'mousedown',
      'mousemove',
      'keypress', 
      'scroll',
      'touchstart',
      'click'
    ];

    events.forEach(event => {
      document.removeEventListener(event, this.handleActivity, true);
    });

    window.removeEventListener('beforeunload', this.handleActivity);
  }

  /**
   * Check if session is still active
   */
  checkActivity() {
    const now = Date.now();
    const timeSinceLastActivity = now - this.lastActivity;
    
    return {
      isActive: this.isActive,
      timeSinceLastActivity,
      timeUntilTimeout: Math.max(0, this.timeoutDuration - timeSinceLastActivity),
      timeUntilWarning: Math.max(0, (this.timeoutDuration - this.warningDuration) - timeSinceLastActivity)
    };
  }

  /**
   * Extend session manually
   */
  extendSession() {
    if (this.isActive) {
      console.log('Session extended manually - reset to 60 minutes');
      this.resetTimer();
    }
  }

  /**
   * Get remaining time in minutes
   */
  getRemainingTime() {
    if (!this.isActive) return 0;
    
    const now = Date.now();
    const elapsed = now - this.lastActivity;
    const remaining = Math.max(0, this.timeoutDuration - elapsed);
    
    return Math.ceil(remaining / (60 * 1000)); // Return in minutes
  }
}

// Create singleton instance
const sessionTimeout = new SessionTimeout();

export default sessionTimeout; 